/**
 * Order deadline computation.
 *
 * Schools provision a per-Location policy that says "orders for the service
 * week are due by Wednesday/Friday prior at 11:59pm local time." This
 * module turns that policy + a specific service date into the exact UTC
 * moment after which ordering closes.
 *
 * Pure TS, no Node-only imports — safe to call from kiosk/families/etc.
 * browser bundles as well as servers.
 */

/**
 * Mirrors the Prisma `DayOfWeek` enum. Duplicated here (string union) so
 * this package stays Prisma-free.
 */
export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY'
export type OrderDeadlineMode = 'FIXED_WEEKLY' | 'RELATIVE_DAYS'

export interface OrderDeadlinePolicy {
  /**
   * FIXED_WEEKLY preserves the historical "Wednesday prior" style policy.
   * RELATIVE_DAYS means "N calendar days before the service date."
   * Undefined defaults to FIXED_WEEKLY for existing callers.
   */
  orderDeadlineMode?: OrderDeadlineMode | null
  /**
   * Day of the week by which orders for the service week must be placed.
   * `null` means no deadline policy is provisioned — `computeOrderDeadline`
   * will return `null` in FIXED_WEEKLY mode.
   */
  orderDeadlineDayOfWeek: DayOfWeek | null
  /**
   * Number of full weeks before the service week. `1` (typical) means
   * "the week before." `0` means "the same week as service." `2` means
   * "two weeks before."
   */
  orderDeadlineWeeksBefore: number
  /**
   * Local time of day, 24-hour "HH:mm". Validated; throws on malformed.
   */
  orderDeadlineTime: string
  /**
   * Calendar days before service for RELATIVE_DAYS mode. 0 = same day,
   * 1 = day before service, etc.
   */
  relativeDaysBeforeService?: number | null
  /**
   * IANA timezone identifier (e.g. `America/New_York`). Determines the
   * UTC moment the wall-clock cutoff resolves to.
   */
  timeZone: string
}

const ISO_WEEKDAY_FOR_DAY: Record<DayOfWeek, number> = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
}

/**
 * The order deadline for a given service date under a Location's policy.
 *
 * Algorithm:
 *   1. Find the Monday of the service week containing `serviceDate`.
 *   2. Step back `weeksBefore` weeks to land on the Monday of the deadline week.
 *   3. Step forward to `orderDeadlineDayOfWeek` within that week.
 *   4. Combine with `orderDeadlineTime` as a wall time in `timeZone`.
 *   5. Resolve to the corresponding UTC instant.
 *
 * Returns `null` when no policy is provisioned (no `orderDeadlineDayOfWeek`).
 *
 * @example
 * computeOrderDeadline(new Date('2026-06-01T00:00:00Z'), {
 *   orderDeadlineDayOfWeek: 'WEDNESDAY',
 *   orderDeadlineWeeksBefore: 1,
 *   orderDeadlineTime: '23:59',
 *   timeZone: 'America/New_York',
 * })
 * // -> Date corresponding to 2026-05-27 23:59 America/New_York (= 2026-05-28T03:59:00Z)
 */
export function computeOrderDeadline(serviceDate: Date, policy: OrderDeadlinePolicy): Date | null {
  const [hh, mm] = parseHourMinute(policy.orderDeadlineTime)
  const mode = policy.orderDeadlineMode ?? 'FIXED_WEEKLY'

  if (mode === 'RELATIVE_DAYS') {
    const daysBefore = Math.max(0, Math.floor(policy.relativeDaysBeforeService ?? 0))
    const relativeCalendar = new Date(
      Date.UTC(serviceDate.getUTCFullYear(), serviceDate.getUTCMonth(), serviceDate.getUTCDate() - daysBefore),
    )

    return wallTimeInZoneToUtc(
      relativeCalendar.getUTCFullYear(),
      relativeCalendar.getUTCMonth() + 1,
      relativeCalendar.getUTCDate(),
      hh,
      mm,
      policy.timeZone,
    )
  }

  if (!policy.orderDeadlineDayOfWeek) {
    return null
  }

  const targetWeekday = ISO_WEEKDAY_FOR_DAY[policy.orderDeadlineDayOfWeek]
  const weeksBefore = Math.max(0, Math.floor(policy.orderDeadlineWeeksBefore))

  // Use the serviceDate's UTC calendar — Prisma stores @db.Date columns as
  // UTC-midnight Dates, so this matches the wire format.
  const serviceY = serviceDate.getUTCFullYear()
  const serviceM = serviceDate.getUTCMonth()
  const serviceD = serviceDate.getUTCDate()
  const serviceWeekday = isoWeekdayUTC(serviceDate)

  // Monday of the service week = serviceDate - (serviceWeekday - 1) days
  // Monday of the deadline week = that - weeksBefore * 7 days
  // Cutoff date = that + (targetWeekday - 1) days
  const totalBackDays = serviceWeekday - 1 + weeksBefore * 7 - (targetWeekday - 1)

  const deadlineCalendar = new Date(Date.UTC(serviceY, serviceM, serviceD - totalBackDays))

  return wallTimeInZoneToUtc(
    deadlineCalendar.getUTCFullYear(),
    deadlineCalendar.getUTCMonth() + 1, // 1-12 for the helper
    deadlineCalendar.getUTCDate(),
    hh,
    mm,
    policy.timeZone,
  )
}

/**
 * The UTC instant of local midnight (00:00) at the START of `serviceDate`'s
 * calendar day in `timeZone`.
 *
 * This is the implicit ordering cutoff when a location has NO deadline policy
 * provisioned: "orders open until service-date midnight" (see `Location`
 * schema). Past service dates must never be orderable, even absent a policy —
 * once `now` reaches this instant, ordering for that day has closed. Uses the
 * `serviceDate`'s UTC calendar day, matching how `computeOrderDeadline` reads
 * the wire format (Prisma `@db.Date` = UTC-midnight).
 */
export function startOfServiceDayInZone(serviceDate: Date, timeZone: string): Date {
  return wallTimeInZoneToUtc(
    serviceDate.getUTCFullYear(),
    serviceDate.getUTCMonth() + 1, // 1-12 for the helper
    serviceDate.getUTCDate(),
    0,
    0,
    timeZone,
  )
}

/**
 * The effective ordering cutoff for `serviceDate` — the instant after which
 * ordering (or editing) for that day is closed.
 *
 * When a deadline policy is provisioned this is `computeOrderDeadline`. When it
 * is NOT (that would return `null`), the cutoff falls back to the service day's
 * local midnight (`startOfServiceDayInZone`): past service dates are never
 * orderable, even absent a policy. Never returns `null`.
 *
 * The single source of truth for "is ordering closed?" across the school and
 * family order gates — callers compare `now` against it.
 */
export function orderingCutoff(serviceDate: Date, policy: OrderDeadlinePolicy): Date {
  return computeOrderDeadline(serviceDate, policy) ?? startOfServiceDayInZone(serviceDate, policy.timeZone)
}

/**
 * `true` if `now` is strictly before the order deadline for `serviceDate`.
 * Convenience wrapper for cart/UI gating code.
 *
 * Returns `true` (unrestricted) when the location has no deadline policy.
 */
export function isOrderingOpen(serviceDate: Date, policy: OrderDeadlinePolicy, now: Date = new Date()): boolean {
  const deadline = computeOrderDeadline(serviceDate, policy)
  if (deadline === null) {
    return true
  }
  return now.getTime() < deadline.getTime()
}

const DAY_FOR_ISO_WEEKDAY: Record<number, DayOfWeek> = {
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
  7: 'SUNDAY',
}

/**
 * The default auto-submit day for subscription auto-ordering: one day before
 * the location's weekly order deadline (deadline Wednesday → auto-submit
 * Tuesday), so still-DRAFT carts are submitted before ordering closes.
 *
 * Falls back to `'THURSDAY'` (the historical schema default) when the
 * location has no weekly deadline day — either no policy provisioned or a
 * RELATIVE_DAYS policy, which has no day-of-week to anchor to.
 */
export function defaultAutoSubmitDow(orderDeadlineDayOfWeek: DayOfWeek | null | undefined): DayOfWeek {
  if (!orderDeadlineDayOfWeek) {
    return 'THURSDAY'
  }
  const deadlineWeekday = ISO_WEEKDAY_FOR_DAY[orderDeadlineDayOfWeek]
  return DAY_FOR_ISO_WEEKDAY[deadlineWeekday === 1 ? 7 : deadlineWeekday - 1]!
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function isoWeekdayUTC(date: Date): number {
  // JS getUTCDay: Sun=0..Sat=6. ISO: Mon=1..Sun=7.
  const js = date.getUTCDay()
  return js === 0 ? 7 : js
}

function parseHourMinute(value: string): [number, number] {
  const match = /^([0-1]?\d|2[0-3]):([0-5]\d)$/.exec(value)
  if (!match) {
    throw new RangeError(`orderDeadlineTime must be "HH:mm" (24h), got "${value}"`)
  }
  return [parseInt(match[1]!, 10), parseInt(match[2]!, 10)]
}

/**
 * Convert a wall-clock moment in a named timezone to a UTC `Date`.
 *
 * No external deps. Works by:
 *   1. Building a tentative UTC instant from the wall-clock fields.
 *   2. Asking Intl.DateTimeFormat what that instant LOOKS like in the
 *      target timezone.
 *   3. The difference is the timezone offset for that wall time; apply it.
 *   4. Re-checking that the resulting instant's wall time MATCHES the
 *      requested wall time — if it doesn't, we hit a DST gap or fold.
 *
 * DST behavior:
 *   - Spring-forward (missing hour, e.g. 02:30 on 2026-03-08 America/New_York):
 *     Intl returns an instant 1 hour forward (03:30 instead of 02:30). We
 *     detect the mismatch and throw `OrderDeadlineDstGapError` so callers
 *     can decide whether to roll forward or warn the operator.
 *   - Fall-back (repeated hour, e.g. 01:30 happens twice): the EARLIER
 *     occurrence wins (safer/stricter cutoff). No error thrown.
 *
 * For 23:59 cutoffs we never hit either case, but configurable times like
 * 02:30 / 17:00 could.
 */
export class OrderDeadlineDstGapError extends RangeError {
  constructor(
    public readonly timeZone: string,
    public readonly requestedWallTime: string,
    public readonly observedWallTime: string,
  ) {
    super(
      `DST gap: wall time ${requestedWallTime} does not exist in ${timeZone} on that date ` +
        `(Intl resolved to ${observedWallTime}). Pick a different cutoff time.`,
    )
    this.name = 'OrderDeadlineDstGapError'
  }
}

function wallTimeInZoneToUtc(
  year: number,
  month: number, // 1-12
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const tentative = Date.UTC(year, month - 1, day, hour, minute)
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const get = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type)
    if (!part) {
      throw new Error(`Intl part "${type}" missing for timezone ${timeZone}`)
    }
    return parseInt(part.value, 10)
  }

  const normalizeHour = (h: number): number => (h === 24 ? 0 : h)

  // Round 1: tentative -> observed offset
  const parts = formatter.formatToParts(new Date(tentative))
  const observed = Date.UTC(
    get(parts, 'year'),
    get(parts, 'month') - 1,
    get(parts, 'day'),
    normalizeHour(get(parts, 'hour')),
    get(parts, 'minute'),
  )
  const offset = tentative - observed
  const utc = tentative + offset

  // Round 2: re-derive the wall time at `utc` and confirm it matches the
  // requested time. If not, we hit a DST gap (spring-forward missing hour).
  const verifyParts = formatter.formatToParts(new Date(utc))
  const verifyHour = normalizeHour(get(verifyParts, 'hour'))
  const verifyMinute = get(verifyParts, 'minute')
  if (verifyHour !== hour || verifyMinute !== minute) {
    const requested = `${pad2(hour)}:${pad2(minute)}`
    const observedStr = `${pad2(verifyHour)}:${pad2(verifyMinute)}`
    throw new OrderDeadlineDstGapError(timeZone, requested, observedStr)
  }

  return new Date(utc)
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`
}
